/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';

import { jsx } from '@atlaskit/css';
import {
	DefaultExtensionProvider,
	type ExtensionManifest,
} from '@atlaskit/editor-common/extensions';
import Lozenge from '@atlaskit/lozenge/lozenge';
import {
	createSkillNode,
	ROVO_SKILL_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
} from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { useAnalyticsPropertiesContext } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics-properties-context';
import { type Skill } from '@atlassian/rovo-platform-types/skills';
import {
	defaultIcon,
	skillIcon,
	type SkillIconName,
} from '@atlassian/rovo-platform-ui-components/skills/skill-icons';

import { createSkillTagNodeRenderer } from './createSkillTagNodeRenderer';

const getSkillIcon = (iconName?: SkillIconName) =>
	iconName && skillIcon[iconName] ? skillIcon[iconName] : defaultIcon;

const SKILL_DESCRIPTION_CHAR_LIMIT = 150;

const truncateSkillDescription = (description: string | null | undefined): string => {
	if (!description) {
		return '';
	}
	if (description.length <= SKILL_DESCRIPTION_CHAR_LIMIT) {
		return description;
	}
	const slice = description.slice(0, SKILL_DESCRIPTION_CHAR_LIMIT);
	const lastWhitespace = slice.search(/\s\S*$/);

	const truncated = lastWhitespace > 0 ? slice.slice(0, lastWhitespace) : slice;
	return `${truncated.trimEnd()}…`;
};

const skillsEditorMessages = defineMessages({
	betaLabel: {
		id: 'conversation-assistant-chat-prompt-input.skills-editor-extension.beta-label',
		defaultMessage: 'Beta',
		description: 'Beta badge label shown next to admin skill actions in the slash command menu',
	},
});

const adminSkillBetaLozenge = (
	<Lozenge appearance="discovery">
		<FormattedMessage {...skillsEditorMessages.betaLabel} />
	</Lozenge>
);

type UseSkillsManifestProps = {
	skills: Skill[];
	isSpecialistPromptInput: boolean;
	promptHasSkill: boolean;
	enableSkills?: boolean;
	siteId?: string;
	disableSkillTagHoverCardInEditor?: boolean;
	skillTagHoverCardMaxHeightInEditor?: number | string;
};

export const useSkillsManifest = ({
	skills,
	isSpecialistPromptInput,
	promptHasSkill,
	enableSkills = true,
	siteId,
	disableSkillTagHoverCardInEditor = false,
	skillTagHoverCardMaxHeightInEditor,
}: UseSkillsManifestProps): DefaultExtensionProvider<Skill>[] | undefined => {
	const { sendAnalyticsEvent } = useAnalytics();
	// Skill selection source is recorded on the AnalyticsPropertiesContext so the chat
	// input can attach it to the next `aiResult viewed` event without needing a
	// per-input store. The provider is mounted once per chat instance, scoping the value
	// to the input that consumes it.
	const { setAnalyticsPropertiesWithoutRerender } = useAnalyticsPropertiesContext();

	const createManifest = React.useCallback(
		(skills: Skill[]) => {
			const manifest: ExtensionManifest<Skill> = {
				title: 'Skills',
				type: ROVO_SKILL_EXTENSION_TYPE,
				key: ROVO_SKILL_EXTENSION_KEY,
				categories: ['Skills'],
				icons: {
					'16': defaultIcon,
					'24': defaultIcon,
					'48': defaultIcon,
				},
				modules: {
					quickInsert: skills.map((skill) => {
						const { slug, description, name, product, iconName, tags } = skill;
						const lozenge = tags?.some((t) => t.toLowerCase() === 'beta')
							? adminSkillBetaLozenge
							: undefined;
						return {
							key: slug,
							keywords: ['skills', 'skill', slug, product ?? ''],
							// 1 is the default priority
							priority: 99,
							title: name,
							description: truncateSkillDescription(description),
							icon: getSkillIcon(iconName),
							lozenge,
							action: async () => {
								sendAnalyticsEvent({
									eventType: 'ui',
									action: 'clicked',
									actionSubject: 'button',
									actionSubjectId: 'rovoChatSkillSelected',
									attributes: {
										slashCommand: slug,
										skillProductKey: product,
										skillSelectionSource: 'editorTypeahead',
									},
								});
								setAnalyticsPropertiesWithoutRerender({
									skillSelectionSource: 'editorTypeahead',
								});
								return createSkillNode(skill);
							},
						};
					}),
					nodes: createSkillTagNodeRenderer(
						siteId,
						disableSkillTagHoverCardInEditor,
						skillTagHoverCardMaxHeightInEditor,
					),
				},
			};

			return manifest;
		},
		[
			sendAnalyticsEvent,
			setAnalyticsPropertiesWithoutRerender,
			siteId,
			disableSkillTagHoverCardInEditor,
			skillTagHoverCardMaxHeightInEditor,
		],
	);

	const manifestProvider = React.useMemo(() => {
		// Return undefined if skills are disabled
		if (!enableSkills) {
			return undefined;
		}
		// skills are only applicable to non-agentic conversations
		// remove skills if a skill is already present in the prompt until chaining is allowed
		if (isSpecialistPromptInput || promptHasSkill) {
			return [new DefaultExtensionProvider([createManifest([])])];
		}
		return [new DefaultExtensionProvider([createManifest(skills)])];
	}, [isSpecialistPromptInput, promptHasSkill, createManifest, enableSkills, skills]);

	return manifestProvider;
};
